import {
  getProductProperty,
  getTwoProductProperty,
} from "@scenes/findMatch/Utils/ProductDetailList/utils";
import currency from "currency.js";
import { colorStringForShort, CustomisationKey } from "../types";
import {
  GlobalEnum,
  ProductCategory,
  ProductTypeCommon,
} from "@utils/constants";
import { useMemo } from "react";
import { DefaultThemeValueType } from "../../../../styled";
import { useTheme } from "styled-components/native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { isPair, isParcel } from "@utils/product/productValidators";
import { SpaceType } from "@components/atoms/Spacer/types";
import {
  formatNumberWithPrecision,
  percent,
} from "@utils/product/productFormatters";
import { getProductName } from "@utils/product/productNames";
import { Diamond } from "@src/types/DiamondDetail";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { useFilteredSection } from "../../../hooks/useFilteredOptions";
import { DefaultTheme } from "styled-components/native";
import { SectionLayout } from "@utils/customization";

export const hasZeroDecimals = (formattedValue: any) => {
  const formattedString = formattedValue?.toString();
  const [, decimalPart] = formattedString?.split(".");
  return (
    decimalPart === undefined || decimalPart === "0" || decimalPart === "00"
  );
};

export const dataMapValidateValue = (
  dataProperty: string,
  productType: string,
  itemDetail: Diamond,
  isPdpScreen: boolean,
  dataType?: string,
  MoreNumber?: boolean,
  showQuantityInHandAsText?: boolean,
  t?: any,
  hideCertOnMatchPair: boolean = false,
  postfix?: string = ""
) => {
  let dataMappedValue = "-";
  switch (dataProperty) {
    case "city":
      dataMappedValue = getTwoProductProperty(
        productType === ProductCategory.JEWELRY
          ? itemDetail?.vendor_city
          : itemDetail?.city,
        "",
        productType === ProductCategory.JEWELRY
          ? itemDetail?.vendor_country
          : itemDetail?.country,
        ""
      );
      break;
    case "depth_percent":
      dataMappedValue = percent(itemDetail?.depth_percent);
      break;
    case "table_percent":
      dataMappedValue = percent(
        currency(itemDetail?.table_percent, {
          symbol: "",
          precision: 0,
        }).format()
      );
      break;
    case "crown_height":
      dataMappedValue = getTwoProductProperty(
        itemDetail?.crown_height,
        "%",
        itemDetail?.crown_angle,
        "°"
      );
      break;
    case "pavilion_depth":
      dataMappedValue = getTwoProductProperty(
        itemDetail?.pavilion_depth,
        "%",
        itemDetail?.pavilion_angle,
        "°"
      );
      break;
    case "color":
      dataMappedValue = colorStringForShort(false, itemDetail);
      break;
    case "lab":
      if (isPdpScreen) {
        dataMappedValue = itemDetail?.lab;
      } else {
        dataMappedValue =
          itemDetail?.lab +
          (!hideCertOnMatchPair &&
          itemDetail?.cert_num &&
          itemDetail?.cert_num !== null &&
          itemDetail?.cert_num !== ""
            ? " #" + itemDetail?.cert_num
            : "");
      }

      break;
    case "Quantity":
      dataMappedValue = itemDetail?.parcel_stones ?? "-";
      break;

    case "Origin":
      dataMappedValue = itemDetail?.inclusion_open ?? "-";
      break;

    case "measLength":
      dataMappedValue = itemDetail?.meas_length
        ? parseFloat(itemDetail?.meas_length) === 0
          ? "-"
          : formatNumberWithPrecision(itemDetail?.meas_length, {
              precision: 2,
            }) ?? "0.00"
        : "-";
      break;

    case "quantity_in_hand":
      const qty = parseInt(itemDetail?.quantity_in_hand ?? "", 10);
      dataMappedValue = isNaN(qty)
        ? "-"
        : qty === 0
        ? showQuantityInHandAsText
          ? t
            ? t(
                "productDetailPage.availableOnRequest.text",
                "Available on request"
              )
            : "Available on request"
          : "0"
        : showQuantityInHandAsText
        ? t
          ? t("productDetailPage.immediateShipping.text", "Immediate Shipping")
          : "Immediate Shipping"
        : String(qty);
      break;

    case "metal_total_weight":
      if (!isNaN(itemDetail?.metal_total_weight ?? 0)) {
        break;
      } else {
        dataMappedValue = itemDetail?.metal_total_weight + " " + postfix;
      }
      break;

    default:
      if (dataType === "hideview" && MoreNumber) return "-";
      const resolvedValue = dataProperty?.includes(".")
        ? dataProperty?.split(".")?.reduce((acc, key) => acc?.[key], itemDetail)
        : itemDetail?.[dataProperty as keyof typeof itemDetail];

      dataMappedValue = getProductProperty(resolvedValue, "");
  }
  return dataMappedValue;
};

export const getLocationProperty = (
  localSelectedLocation: any,
  key: string,
  preFix: string
) => {
  let propertyValue = "-";

  if (localSelectedLocation && localSelectedLocation[preFix]) {
    propertyValue =
      "+" + localSelectedLocation[preFix] + " " + localSelectedLocation[key];
  } else if (localSelectedLocation) {
    propertyValue = localSelectedLocation[key];
  }
  return propertyValue;
};
export const getProductAlphaColor = (productType: string | undefined) => {
  switch (productType) {
    case ProductCategory.DIAMOND:
      return "diam-03";
    case ProductCategory.GEMSTONE:
      return "gem-03";
    case ProductCategory.JEWELRY:
      return "jewelry-03";
    case ProductCategory.LAB_GROWN_DIAMOND:
      return "lab-03";
    case ProductCategory.LABGROWN_JEWELRY:
      return "lab-03";
    default:
      return "diam-03";
  }
};

export const checkValidationValue = (
  localSelectedLocation: any,
  showNumber: any,
  key: string
) => {
  if (localSelectedLocation) {
    if (localSelectedLocation[key]) {
      return showNumber;
    }
  }
  return false;
};

export const checkSideStoneValue = (itemDetail: any, currentItem?: string) => {
  if (
    getProductProperty(itemDetail?.side_stone_shape, "") === "-" &&
    getProductProperty(itemDetail?.side_stone_color, "") === "-"
  ) {
    return false;
  }

  if (
    currentItem !== undefined &&
    getProductProperty(currentItem, "") === "-"
  ) {
    return false;
  }
  return true;
};

export const checkCenterStoneValue = (
  itemDetail?: any,
  currentItem?: string
) => {
  if (
    getProductProperty(itemDetail?.center_shape, "") === "-" &&
    getProductProperty(itemDetail?.center_size, "") === "-" &&
    getProductProperty(itemDetail?.center_color, "") === "-" &&
    getProductProperty(itemDetail?.center_clarity, "") === "-"
  ) {
    return false;
  }

  if (
    currentItem !== undefined &&
    getProductProperty(currentItem, "") === "-"
  ) {
    return false;
  }
  return true;
};

export const checkProductDetailValue = (
  itemDetail?: any,
  currentItem?: string,
  currentSecondItem?: string
) => {
  if (
    getProductProperty(itemDetail?.jewelry_style, "") === "-" &&
    getProductProperty(itemDetail?.size, "") === "-" &&
    getProductProperty(itemDetail?.metals, "") === "-" &&
    getProductProperty(itemDetail?.brand, "") === "-" &&
    getProductProperty(itemDetail?.city, "") === "-" &&
    getProductProperty(itemDetail?.country, "") === "-" &&
    getProductProperty(itemDetail?.jewelry_description, "") === "-"
  ) {
    return false;
  }
  if (
    currentItem !== undefined &&
    getProductProperty(currentItem, "") === "-"
  ) {
    return false;
  }
  if (
    currentSecondItem !== undefined &&
    getProductProperty(currentSecondItem, "") === "-"
  ) {
    return false;
  }
  return true;
};

export const filterCertificate = (mediaItem: any) => {
  return mediaItem?.filter(
    (item: any) => item?.type === "image" || item?.type === "video"
  );
};

export const filterInvalidMedia = (mediaArray: any) => {
  return mediaArray?.filter((item) => {
    // Check if the media item is a video and has a valid link (not "1")
    if (
      item.type === "video" &&
      !(item.link.includes("http://") || item.link.includes("https://"))
    ) {
      return false;
    }
    return true;
  });
};

export const getProductColor = (
  productType: string | undefined
): DefaultThemeValueType => {
  switch (productType) {
    case ProductCategory.DIAMOND:
      return "diam-06";
    case ProductCategory.GEMSTONE:
      return "gem-06";
    case ProductCategory.JEWELRY:
      return "jewelry-06";
    case ProductCategory.LAB_GROWN_DIAMOND:
      return "lab-06";
    case ProductCategory.LABGROWN_JEWELRY:
      return "lab-06";
    default:
      return "diam-06";
  }
};

export const useProductInfo = (
  t: any,
  productType?: string,
  itemDetail?: any,
  isCustomApp?: boolean,
  fromOrderCart?: boolean
) => {
  return useMemo(() => {
    const pairType = t("productDetail.pair.text", "Pair");
    const parcelType = t("productDetail.parcels.text", "Parcels");
    let productName = (() => {
      switch (productType) {
        case ProductCategory.DIAMOND:
          return t("global.text.naturalDiamonds", "Natural Diamonds");
        case ProductCategory.GEMSTONE:
          return t("global.text.coloredGemstones", "Colored Gemstones");
        case ProductCategory.JEWELRY:
          return itemDetail &&
            itemDetail?.sub_type === ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE
            ? getProductName(ProductCategory.LABGROWN_JEWELRY, t)
            : t("global.text.jewelry", "Jewelry");
        case ProductCategory.LAB_GROWN_DIAMOND:
          return t("global.text.labGrownDiamonds", "Lab-Grown Diamonds");
        case ProductCategory.LABGROWN_JEWELRY:
          return t("global.text.labGrownJewelry", "Lab-Grown Jewelry");
        default:
          return t("global.text.naturalDiamonds", "Natural Diamonds");
      }
    })();
    if (isPair(fromOrderCart ? itemDetail?.item : itemDetail)) {
      productName = `${productName} ${pairType}`;
    } else if (
      isCustomApp &&
      isParcel(fromOrderCart ? itemDetail?.item : itemDetail)
    ) {
      productName = `${productName} ${parcelType}`;
    }
    return {
      productName,
    };
  }, [productType, itemDetail, t]);
};

export const getAvailabilityStatus = (available?: number) => {
  const theme = useTheme();
  const userInfo = useUserInfo();

  let colorKey;
  switch (available) {
    case GlobalEnum.OUT_ON_MEMO:
      colorKey = "error-01";
      break;
    case GlobalEnum.ON_HAND:
      colorKey = "success-01";
      break;
    case GlobalEnum.PLEASE_EMAIL:
    default:
      colorKey = "warn-01";
      break;
  }

  return {
    color: theme[colorKey],
    text: userInfo?.onHandText(available ?? GlobalEnum.OUT_ON_MEMO),
    colorKey: colorKey,
  };
};

export const checkAllFieldsEntered = (
  option_value: any,
  option_required: any
) => {
  if (option_value && option_required) {
    let requiredOptionsFail = false;
    let requiredFields = Object.keys(option_required);
    requiredFields = requiredFields.filter(
      (option) => option_required[option] === true
    );
    const optionValueKeys = Object.keys(option_value).filter(
      (item) => option_value[item] != null
    );
    requiredOptionsFail = requiredFields.some(
      (item) => !optionValueKeys.includes(item)
    );
    return requiredOptionsFail;
  }
  return false;
};

export const getComponentDimentions = (theme, height, width) => {
  const sizes = {
    imageWidth: 344,
    thumbWidth: 56,
    iconWidth: 24,
    spacing: SpaceType.Extra,
  };

  if (theme.isLargeScreen && height > 720) {
    if (width >= 1440 && width <= 1920) {
      //imageWidth = 480 + ((width - 1440) / (1920 - 1440)) * (640 - 480); // Backup code smooth scale
      const progressRatio = (width - 1440) / (1920 - 1440);

      const baseWidth = 480;
      const targetWidth = baseWidth * 1.3333;
      sizes.imageWidth = baseWidth + progressRatio * (targetWidth - baseWidth);
    } else if (width > 1920) {
      sizes.imageWidth = 640;
      sizes.spacing = SpaceType.Double;
    }
  }

  if (theme.isLargeScreen) {
    if (width >= 1440 && width <= 1920) {
      //imageWidth = 480 + ((width - 1440) / (1920 - 1440)) * (640 - 480); // Backup code smooth scale
      const progressRatio = (width - 1440) / (1920 - 1440);

      const baseWidth = 72;
      const targetWidth = baseWidth * 1.3333;
      sizes.thumbWidth = baseWidth + progressRatio * (targetWidth - baseWidth);
    } else if (width > 1920) {
      sizes.thumbWidth = 96;
    }
  }

  if (theme.isLargeScreen) {
    if (width >= 1440 && width <= 1920) {
      //imageWidth = 480 + ((width - 1440) / (1920 - 1440)) * (640 - 480); // Backup code smooth scale
      const progressRatio = (width - 1440) / (1920 - 1440);

      const baseWidth = 30;
      const targetWidth = baseWidth * 1.3333;
      sizes.iconWidth = baseWidth + progressRatio * (targetWidth - baseWidth);
    } else if (width > 1920) {
      sizes.iconWidth = 42;
    }
  }

  return { sizes };
};
export const getAddToJewelryOptionsDataOptions = (
  options?: any,
  getAddToJewelryOptionsData?: any
) => {
  if (options) {
    return getAddToJewelryOptionsData?.data?.add_to_jewelry?.options?.reduce(
      (acc, item) => {
        acc[item.name] = {
          ...item,
          price_modes: [0, 1, 2],
          label: item?.long_name || "",
        };
        return acc;
      },
      {}
    );
  } else {
    return {};
  }
};

export const getChooseStoneOptions = (options?: any) => {
  if (options) {
    return options?.reduce((acc, item) => {
      acc[item.name] = {
        ...item,
        label: item?.long_name || "",
      };
      return acc;
    }, {});
  } else {
    return {};
  }
};

export const reorderForColumnFirst = <T>(
  data: T[],
  columnCount: number
): T[] => {
  const staticItems: { index: number; item: T }[] = [];
  const dynamicItems: T[] = [];

  data.forEach((item, index) => {
    if (item?.isSingleRow) {
      staticItems.push({ index, item });
    } else {
      dynamicItems.push(item);
    }
  });

  const rowCount = Math.ceil(dynamicItems.length / columnCount);
  const reordered: T[] = [];

  // Reorder dynamic items column-first
  for (let row = 0; row < rowCount; row++) {
    for (let col = 0; col < columnCount; col++) {
      const index = col * rowCount + row;
      if (index < dynamicItems.length) {
        reordered.push(dynamicItems[index]);
      }
    }
  }

  // Reinsert static items at original positions
  staticItems.forEach(({ index, item }) => {
    reordered.splice(index, 0, item);
  });

  return reordered;
};

export const getCommentData = (
  itemComments: string,
  itemDetail: Diamond,
  priceMode: PriceModes,
  showSupplierSpecificCommentInOrangeMode: boolean,
  t: any
) => {
  let commentData: string;
  const supplierData = showSupplierSpecificCommentInOrangeMode
    ? itemDetail?.supplier_specific_comment
    : "";

  let deliveryStr = itemDetail?.estimated_delivery
    ? ` | ${t(
        "productDetailPage.estimatedDelivery.text",
        "Estimated Delivery:"
      )} ${itemDetail?.estimated_delivery ?? ""}`
    : "";

  const isCommentEmpty = !itemComments || itemComments === "-";

  const certComments = itemDetail?.cert_comment || null;
  let supplierStr = supplierData ? ` | ${supplierData}` : "";

  const isSupplierEmpty = !supplierData;

  if (isCommentEmpty) {
    supplierStr = supplierStr.replace(/^\s*\|\s*/, "");
  }

  if (isCommentEmpty && isSupplierEmpty) {
    deliveryStr = deliveryStr.replace(/^\s*\|\s*/, "");
  }

  if (itemComments && certComments) {
    if (itemComments === certComments && priceMode === PriceModes.ORANGE) {
      const commentDataRaw =
        (itemComments !== "-" ? itemComments : "") + supplierStr + deliveryStr;
      commentData = commentDataRaw?.trim() ? commentDataRaw : "-";
    } else {
      commentData = itemComments;
    }
  } else if (itemComments && priceMode === PriceModes.ORANGE) {
    const commentDataRaw =
      (itemComments !== "-" ? itemComments : "") + supplierStr + deliveryStr;
    commentData = commentDataRaw?.trim() ? commentDataRaw : "-";
  } else if (certComments && priceMode === PriceModes.ORANGE) {
    const commentDataRaw = certComments + supplierStr + deliveryStr;
    commentData = commentDataRaw?.trim() ? commentDataRaw : "-";
  } else if (
    getProductProperty(itemDetail?.supplier_specific_comment, "") &&
    priceMode === PriceModes.ORANGE
  ) {
    commentData = itemDetail?.supplier_specific_comment ?? "" + deliveryStr;
  } else if (
    getProductProperty(itemDetail?.estimated_delivery, "-") &&
    priceMode === PriceModes.ORANGE
  ) {
    commentData = `${t(
      "productDetailPage.estimatedDelivery.text",
      "Estimated Delivery:"
    )} ${itemDetail?.estimated_delivery ?? "-"}`;
  } else {
    commentData = itemComments ?? "-";
  }
  return commentData;
};

export const getPDPHeaderCustomisation = (
  theme: DefaultTheme,
  priceMode: number,
  sectionLayout: SectionLayout,
  key: CustomisationKey
) => {
  const filteredSections = useFilteredSection(theme, priceMode, sectionLayout);

  const getFilterDetailData = (searchText: string): any => {
    const filteredArray = filteredSections.filter(
      (item) => item.name === searchText
    );

    return filteredArray;
  };

  return getFilterDetailData(key);
};
